import { useEffect, useLayoutEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { TeknikaMark, setPageTitle, useSlugBrand } from '../components/Brand';
import { Link } from 'react-router-dom';
import { ApiError } from '../api';
import { useAuth } from '../auth';
import { applyBusinessTheme, businessLogo } from '../businessTheme';
import { devDemo } from '../devSignIn';
import { lastBusiness, rememberBusiness } from '../lastBusiness';
import { useSite } from '../site';

/**
 * The office sign-in. It is the business's own: the one in the address
 * (`/login/:slug`), else the one last signed in to on this device, so signing out
 * or opening a saved link lands on it without changing the address. Teknika CRM's
 * generic sign-in, with a Business field, is only the fallback for a first visit or
 * for "Use a different business". On a business's own domain (`useSite`) the domain
 * decides, and there is no other business to choose.
 */
export function Login() {
  const { signIn } = useAuth();
  const site = useSite();
  const params = useParams<{ slug?: string }>();
  const [choosing, setChoosing] = useState(false);
  const domainSlug = site?.slug;
  const slug = domainSlug || (choosing ? undefined : params.slug || lastBusiness() || undefined);
  const slugBrand = useSlugBrand(domainSlug ? undefined : slug);
  const ownBrand = domainSlug ? { name: site?.name ?? '', logo: site?.logo ?? '' } : slugBrand;
  const logo = ownBrand ? businessLogo(slug ?? '', ownBrand.logo) : '';
  // Nothing business-specific until the site is known: on a business's own domain, a slug in
  // the path or the last business on this device is not its business, and its name or
  // colours must not show even for a moment.
  const known = site !== undefined;
  useEffect(
    () => setPageTitle(known && slug ? ownBrand?.name : undefined),
    [known, slug, ownBrand?.name],
  );
  // Layout effect, not the usual effect: it must land before the browser paints, or the
  // default Teknika green flashes before the business's own colours take over.
  useLayoutEffect(() => {
    applyBusinessTheme(known ? (slug ?? '') : '');
    return () => applyBusinessTheme('');
  }, [known, slug]);
  // While developing, the generic sign-in starts on the demo business (see devSignIn.ts)...
  const dev = devDemo();
  const [business, setBusiness] = useState(dev && !slug ? dev.business : '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // ...and signing in to it is filled in, so long as nothing has been typed yet.
  const demo = !!dev && (slug ?? business).trim() === dev.business;
  useEffect(() => {
    const d = devDemo();
    if (!d || !demo) return;
    setEmail((e) => e || d.email);
    setPassword((p) => p || d.password);
  }, [demo]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    const chosen = (slug ?? business).trim();
    try {
      await signIn(chosen, email.trim(), password);
      // A business's own domain always knows its business; only the shared address needs reminding.
      if (!domainSlug) rememberBusiness(chosen);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the server');
      setBusy(false);
    }
  }

  // Wait for the site rather than flash the generic form on a business's own domain.
  if (site === undefined) return null;

  return (
    <form className="card login" onSubmit={submit}>
      {logo ? (
        <img className="login-logo" src={logo} alt={ownBrand?.name} />
      ) : (
        <TeknikaMark size="2.9rem" />
      )}
      <h1>Sign in to {(slug && ownBrand?.name) || 'Teknika CRM'}</h1>
      {!slug && (
        <label>
          Business
          <input value={business} onChange={(e) => setBusiness(e.target.value)} required />
        </label>
      )}
      <label>
        Email
        <input
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </label>
      <label>
        Password
        <input
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
      </label>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      <button type="submit" disabled={busy}>
        {busy ? 'Signing in…' : 'Sign in'}
      </button>
      <Link
        to={slug ? `/forgot-password?business=${encodeURIComponent(slug)}` : '/forgot-password'}
      >
        Forgot your password?
      </Link>
      {slug && !domainSlug && (
        <button
          type="button"
          className="link"
          onClick={() => {
            setChoosing(true);
            setError('');
          }}
        >
          {ownBrand?.name
            ? `Not ${ownBrand.name}? Use a different business`
            : 'Use a different business'}
        </button>
      )}
    </form>
  );
}
